// src/components/admin/BlogAdminPanel.js
import React, { useState, useEffect } from 'react';

const BlogAdminPanel = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    published: false
  });
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch blog posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`/api/admin/blog?page=${currentPage}`, {
          credentials: 'include'
        });
        
        if (response.status === 403) {
          throw new Error('Unauthorized: Admin access required');
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        
        const data = await response.json();
        setPosts(data.posts);
        setTotalPages(data.totalPages);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError(err.message || 'Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [currentPage]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Generate slug from title
  const generateSlug = () => {
    if (formData.title) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  // Load post for editing
  const loadPostForEditing = async (postId) => {
    try {
      setLoading(true);
      
      const response = await fetch(`/api/admin/blog/${postId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch post details');
      }
      
      const post = await response.json();
      
      setFormData({
        title: post.title,
        slug: post.slug,
        content: post.content,
        excerpt: post.excerpt || '',
        featured_image: post.featured_image || '',
        published: post.published || false
      });
      
      setEditingPost(post.id);
    } catch (err) {
      console.error('Error loading post for editing:', err);
      setError(err.message || 'Failed to load post details');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage('');
      
      const method = editingPost ? 'PUT' : 'POST';
      const url = editingPost ? `/api/admin/blog/${editingPost}` : '/api/admin/blog';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save blog post');
      }
      
      await response.json();
      
      // Update the posts list
      const fetchPosts = async () => {
        const response = await fetch(`/api/admin/blog?page=${currentPage}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to refresh blog posts');
        }
        
        const data = await response.json();
        setPosts(data.posts);
        setTotalPages(data.totalPages);
      };
      
      await fetchPosts();
      
      // Reset form and show success message
      resetForm();
      setSuccessMessage(editingPost ? 'Post updated successfully!' : 'New post created successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving blog post:', err);
      setError(err.message || 'Failed to save blog post');
    } finally {
      setLoading(false);
    }
  };

  // Delete a post
  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(`/api/admin/blog/${postId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete post');
      }
      
      // Update the posts list
      setPosts(posts.filter(post => post.id !== postId));
      
      // If we were editing this post, reset the form
      if (editingPost === postId) {
        resetForm();
      }
      
      setSuccessMessage('Post deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting post:', err);
      setError(err.message || 'Failed to delete post');
    } finally {
      setLoading(false);
    }
  };

  // Reset the form
  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      featured_image: '',
      published: false
    });
    setEditingPost(null);
  };

  return (
    <div className="mb-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-blue-300">Blog Management</h2>
        <button
          onClick={resetForm}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          {editingPost ? 'Cancel Editing' : 'New Post'}
        </button>
      </div>
      
      {/* Success message */}
      {successMessage && (
        <div className="mb-4 bg-green-900/30 border border-green-700 p-3 rounded-md text-green-200">
          {successMessage}
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="mb-4 bg-red-900/30 border border-red-700 p-3 rounded-md text-red-200">
          {error}
        </div>
      )}
      
      {/* Blog Post Form */}
      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-8">
        <h3 className="text-lg font-medium mb-4 text-blue-300">
          {editingPost ? 'Edit Post' : 'Create New Post'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              onBlur={generateSlug}
              required
              className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-300 mb-1">
              Slug *
            </label>
            <div className="flex">
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                required
                className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={generateSlug}
                className="ml-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600"
              >
                Generate
              </button>
            </div>
          </div>
          
          {/* Featured Image */}
          <div>
            <label htmlFor="featured_image" className="block text-sm font-medium text-gray-300 mb-1">
              Featured Image (filename)
            </label>
            <input
              type="text"
              id="featured_image"
              name="featured_image"
              value={formData.featured_image}
              onChange={handleInputChange}
              className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Published */}
          <div className="flex items-center h-full pt-6">
            <input
              type="checkbox"
              id="published"
              name="published"
              checked={formData.published}
              onChange={handleInputChange}
              className="h-4 w-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
            />
            <label htmlFor="published" className="ml-2 block text-sm font-medium text-gray-300">
              Published
            </label>
          </div>
        </div>
        
        {/* Excerpt */}
        <div className="mb-4">
          <label htmlFor="excerpt" className="block text-sm font-medium text-gray-300 mb-1">
            Excerpt
          </label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            rows="3"
            className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          ></textarea>
        </div>
        
        {/* Content */}
        <div className="mb-4">
          <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-1">
            Content *
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            rows="10"
            required
            className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          ></textarea>
          <p className="mt-1 text-xs text-gray-400">HTML formatting is supported</p>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white ${
              loading
                ? 'bg-blue-700 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading 
              ? (editingPost ? 'Saving...' : 'Creating...') 
              : (editingPost ? 'Update Post' : 'Create Post')}
          </button>
        </div>
      </form>
      
      {/* Blog Posts List */}
      <div>
        <h3 className="text-lg font-medium mb-4 text-blue-300">All Posts</h3>
        
        {loading && !editingPost ? (
          <div className="text-center py-6">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-2">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No blog posts found. Create your first post!</p>
          </div>
        ) : (
          <>
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Title</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Date</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Author</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Status</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {posts.map(post => (
                      <tr key={post.id} className="hover:bg-gray-750">
                        <td className="py-3 px-4 text-gray-300">
                          <a 
                            href={`/blog/${post.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:text-blue-400"
                          >
                            {post.title}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-gray-400">
                          {formatDate(post.created_at)}
                        </td>
                        <td className="py-3 px-4 text-gray-400">
                          {post.author || 'Admin'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            post.published 
                              ? 'bg-green-900/30 text-green-300' 
                              : 'bg-gray-700 text-gray-300'
                          }`}>
                            {post.published ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => loadPostForEditing(post.id)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center py-4">
                <div className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage <= 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage <= 1
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage >= totalPages
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogAdminPanel;